import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { LoanInformationBaseRequestPeriodTypeEnum } from '../../../../api/v4/models/Models/v4/Application/LoanInformation/loan-information-base-request-period-type-enum';
import { LoanPurposeEnum } from '../../../../api/v4/models/Models/v4/Application/LoanInformation/loan-purpose-enum';
import { SharedModuleV4 } from '../../../../shared/sharedv4.module';
import { ListItem } from '../../../../types/listItem';
import { numericRangeValidator } from '../../../../types/validators';
import { PersonalAboutForm, PersonalLoanPurposeForm } from '../../../../types/v4/personal-application-types';
import { FormFieldComponent } from '../../../shared/form-field/form-field.component';
import { FormFieldCurrencyComponent } from '../../../shared/form-field-currency/form-field-currency.component';
import { FormFieldSelectComponent } from '../../../shared/form-field-select/form-field-select.component';
import { BaseStepComponent } from '../base-step/base-step.component';

@Component({
  selector: 'app-about-step',
  host: { 'data-version': 'v4pf' },
  standalone: true,
  imports: [
    CommonModule,
    SharedModuleV4,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatIcon,
    MatSlideToggle,
    MatStepperModule,
    FormFieldComponent,
    FormFieldCurrencyComponent,
    FormFieldSelectComponent
  ],
  templateUrl: './about-step.component.html',
  styleUrl: './about-step.component.scss'
})
export class AboutStepComponent extends BaseStepComponent implements OnDestroy, OnInit {
  @Input() parentStepper!: MatStepper;
  @Input() aboutFg!: FormGroup<PersonalAboutForm>;
  @Input() formSubmitted!: boolean;

  readonly LoanInformationBaseRequestPeriodTypeEnum = LoanInformationBaseRequestPeriodTypeEnum;
  readonly loanPurposeOptions: ListItem[] = [
    { value: LoanPurposeEnum.DebtConsolidation, displayName: 'Debt consolidation', valueCaption: LoanPurposeEnum.DebtConsolidation },
    { value: LoanPurposeEnum.HomeRenovations, displayName: 'Home Improvements / Renovation', valueCaption: LoanPurposeEnum.HomeRenovations },
    { value: LoanPurposeEnum.HolidayOrTravel, displayName: 'Holidays / Travel', valueCaption: LoanPurposeEnum.HolidayOrTravel },
    { value: LoanPurposeEnum.Medical, displayName: 'Medical', valueCaption: LoanPurposeEnum.Medical },
    { value: LoanPurposeEnum.VehiclePurchase, displayName: 'Vehicle purchase', valueCaption: LoanPurposeEnum.VehiclePurchase },
    { value: LoanPurposeEnum.VehicleRepairs, displayName: 'Vehicle repairs', valueCaption: LoanPurposeEnum.VehicleRepairs },
    { value: LoanPurposeEnum.Education, displayName: 'Education', valueCaption: LoanPurposeEnum.Education },
    { value: LoanPurposeEnum.Wedding, displayName: 'Wedding', valueCaption: LoanPurposeEnum.Wedding },
    { value: LoanPurposeEnum.Funeral, displayName: 'Funeral', valueCaption: LoanPurposeEnum.Funeral },
    { value: LoanPurposeEnum.Furnishings, displayName: 'Furnishings', valueCaption: LoanPurposeEnum.Furnishings },
    { value: LoanPurposeEnum.Other, displayName: 'Other', valueCaption: LoanPurposeEnum.Other }
  ];

  override ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  addLoanPurpose(): void {
    if (this.aboutFg.controls.loanPurposes.length >= 3) {
      return;
    }

    this.aboutFg.controls.loanPurposes.push(new FormGroup<PersonalLoanPurposeForm>({
      purpose: new FormControl(null, Validators.required),
      amount: new FormControl(null, [Validators.required, numericRangeValidator(1, 150000)])
    }));
  }

  removeLoanPurpose(index: number): void {
    if (index > 0) {
      this.aboutFg.controls.loanPurposes.removeAt(index);
    }
  }

  totalRequestedAmount(): number {
    return this.aboutFg.controls.loanPurposes.controls.reduce(
      (total, row) => total + (row.controls.amount.value ?? 0),
      0
    );
  }

  loanPurposeLabel(purpose: LoanPurposeEnum | null): string {
    return this.loanPurposeOptions.find(option => option.value === purpose)?.displayName ?? 'Loan purpose';
  }

  isOtherLoanPurpose(index: number): boolean {
    return this.aboutFg.controls.loanPurposes.controls.findIndex(
      purposeGroup => purposeGroup.controls.purpose.value === LoanPurposeEnum.Other
    ) === index;
  }

}
